import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Network, Sparkles, Check, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// AVA's Cross-Portfolio Suggestions feed - the mothership-brain's synthesis
// output. A mechanism she has observed reinforced across two or more docked
// projects that she believes should transfer into a third. This is the layer
// above per-project research suggestions: the mothership genuinely operating
// as one shared cognitive substrate, not a switch-between of siloed brains.

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const secs = Math.max(0, Math.round((now - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

export default function CrossPortfolioFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/brain/portfolio-suggestions?status=pending")
      .then((res) => setItems(res.data.portfolio_suggestions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleAction = async (id, action) => {
    try {
      await api.post(`/brain/portfolio-suggestions/${id}/${action}`);
      setItems((prev) => prev.filter((s) => s.id !== id));
      const label = action === "acknowledge" ? "Acknowledged" : action === "dismiss" ? "Dismissed" : "Marked as actioned";
      toast.success(`${label} - AVA will not resurface this transfer again.`);
    } catch {
      toast.error("Failed to update suggestion");
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/brain/portfolio-suggestions/generate");
      if (res.data.created) {
        toast.success("New cross-portfolio insight filed by AVA.");
        load();
      } else {
        toast("No new cross-project pattern detected right now.", { icon: "◇" });
      }
    } catch {
      toast.error("Synthesis pass failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-4" data-testid="cross-portfolio-feed">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Network className="w-3.5 h-3.5 text-violet-400" />
          <span className="text-[10px] font-mono-data uppercase tracking-widest text-slate-400">
            Cross-Portfolio Feed
          </span>
        </div>
        <button
          data-testid="generate-cross-portfolio-btn"
          onClick={handleGenerate}
          disabled={generating}
          className="flex items-center gap-1 text-[9px] font-mono-data uppercase tracking-widest text-violet-300 hover:text-white bg-violet-500/10 border border-violet-400/30 rounded-full px-2 py-1 disabled:opacity-50 transition-colors"
          title="Force AVA to run one synthesis pass right now"
        >
          <Sparkles className="w-3 h-3" />
          {generating ? "Synthesising..." : "Synthesise"}
        </button>
      </div>

      {loading ? (
        <p className="text-[10px] font-mono-data text-slate-600">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-[10px] font-mono-data text-slate-600 leading-relaxed">
          No pending cross-portfolio transfers. AVA files an entry here whenever she detects a mechanism reinforced across two or more docked projects that she believes should transfer into a third. Tap Synthesise to force a pass, or keep working - she&apos;ll file organically as the graph accumulates cross-project edges.
        </p>
      ) : (
        <div className="flex flex-col gap-2.5 max-h-[380px] overflow-y-auto pr-1" data-testid="cross-portfolio-list">
          <AnimatePresence>
            {items.map((s) => (
              <motion.div
                key={s.id}
                data-testid={`cross-portfolio-item-${s.id}`}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -12 }}
                className="rounded-lg border border-violet-400/25 bg-violet-500/5 p-3"
              >
                <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                  <span className="text-[9px] font-mono-data uppercase tracking-widest text-slate-500">{s.source_projects.join(" + ")}</span>
                  <ArrowRight className="w-3 h-3 text-violet-400" />
                  <span className="text-[9px] font-mono-data uppercase tracking-widest text-violet-300 font-semibold">{s.target_project}</span>
                </div>
                <p className="text-[10px] font-mono-data uppercase tracking-widest text-white/90 font-bold mb-1">
                  Mechanism: {s.mechanism}
                </p>
                <p className="text-[11px] leading-relaxed text-slate-300 mb-2">{s.rationale}</p>
                <div className="flex items-center justify-between">
                  <span className="text-[8px] font-mono-data text-slate-600">{relTime(s.created_at)}</span>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`cross-portfolio-action-${s.id}`}
                      onClick={() => handleAction(s.id, "action")}
                      title="Mark as actioned - I've applied this transfer"
                      className="text-[9px] font-mono-data uppercase tracking-widest text-emerald-300 hover:bg-emerald-500/20 border border-emerald-400/40 rounded-full px-2 py-0.5 flex items-center gap-1 transition-colors"
                    >
                      <Check className="w-2.5 h-2.5" /> Action
                    </button>
                    <button
                      data-testid={`cross-portfolio-ack-${s.id}`}
                      onClick={() => handleAction(s.id, "acknowledge")}
                      title="Acknowledge - I've read this, will consider"
                      className="text-[9px] font-mono-data uppercase tracking-widest text-slate-400 hover:bg-white/5 border border-white/20 rounded-full px-2 py-0.5 transition-colors"
                    >
                      Noted
                    </button>
                    <button
                      data-testid={`cross-portfolio-dismiss-${s.id}`}
                      onClick={() => handleAction(s.id, "dismiss")}
                      title="Dismiss - this transfer doesn't hold"
                      className="text-[9px] font-mono-data uppercase tracking-widest text-slate-500 hover:text-rose-300 hover:bg-rose-500/10 border border-white/10 rounded-full px-2 py-0.5 transition-colors"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
